<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faChartLine,
  faUsers,
  faClipboardList,
  faBoxes,
  faBell,
  faCog,
  faCalendarAlt,
  faTruck,
  faCheckCircle,
  faClock,
} from '@fortawesome/free-solid-svg-icons'
import WelcomeBanner from '@/components/farm/supervisor/WelcomeBanner.vue'
import AIProjectionCard from '@/components/ai/AIProjectionCard.vue'
import { dashboardService, type SupervisorDashboardData } from '@/api/dashboard'

library.add(
  faChartLine,
  faUsers,
  faClipboardList,
  faBoxes,
  faBell,
  faCog,
  faCalendarAlt,
  faTruck,
  faCheckCircle,
  faClock,
)

const authStore = useAuthStore()
const router = useRouter()
const loading = ref(true)
const dashboardData = ref<SupervisorDashboardData | null>(null)

const stats = ref([
  {
    key: 'total_orders',
    name: 'Total Orders',
    value: '0',
    change: '0%',
    changeType: 'increase',
    icon: 'clipboard-list',
    color: 'bg-blue-500',
  },
  {
    key: 'inventory_items',
    name: 'Inventory Items',
    value: '0',
    change: '0%',
    changeType: 'increase',
    icon: 'boxes',
    color: 'bg-amber-500',
  },
  {
    key: 'pending_tasks',
    name: 'Pending Tasks',
    value: '0',
    change: '0',
    changeType: 'decrease',
    icon: 'calendar-alt',
    color: 'bg-rose-500',
  },
  {
    key: 'sales_today',
    name: 'Sales Today',
    value: '0',
    change: '0',
    changeType: 'increase',
    icon: 'truck',
    color: 'bg-emerald-500',
  },
])

const fetchDashboardData = async () => {
  try {
    loading.value = true
    const data = await dashboardService.getSupervisorStats()
    dashboardData.value = data

    // Update stats values
    stats.value = stats.value.map((s) => {
      const val = data.stats[s.key as keyof typeof data.stats]
      const change = data.stats[`${s.key}_change` as keyof typeof data.stats]
      return {
        ...s,
        value: val.toString(),
        change: change.toString(),
        changeType: change.toString().includes('-') ? 'decrease' : 'increase',
      }
    })
  } catch (error) {
    console.error('Failed to fetch dashboard data:', error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  if (!authStore.isAuthenticated || (authStore.user?.role !== 'SUPERVISOR' && authStore.user?.role !== 'ADMIN')) {
    router.push('/login')
    return
  }
  fetchDashboardData()
})
</script>

<template>
  <div class="min-h-screen bg-slate-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <WelcomeBanner />

      <!-- AI Farm Profit & Yield Projections Card -->
      <AIProjectionCard class="mb-8 mt-6" />

      <!-- Stats Cards -->
      <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div
          v-for="stat in stats"
          :key="stat.name"
          class="relative bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300 overflow-hidden group"
        >
          <div
            class="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 rounded-full opacity-5 group-hover:scale-110 transition-transform duration-500"
            :class="stat.color"
          ></div>
          <div class="flex items-center">
            <div :class="[stat.color, 'p-3 rounded-xl shadow-lg shadow-opacity-20']">
              <FontAwesomeIcon :icon="stat.icon" class="h-6 w-6 text-white" />
            </div>
            <div class="ml-4">
              <p class="text-sm font-semibold text-slate-500 uppercase tracking-wider">
                {{ stat.name }}
              </p>
              <h3 class="text-2xl font-bold text-slate-900 mt-1">
                {{ loading ? '...' : stat.value }}
              </h3>
            </div>
          </div>
          <div class="mt-4 flex items-center">
            <span
              :class="[
                stat.changeType === 'increase' ? 'text-emerald-600 bg-emerald-50' : 'text-rose-600 bg-rose-50',
                'px-2.5 py-0.5 rounded-full text-xs font-bold'
              ]"
            >
              {{ stat.change }}
            </span>
            <span class="ml-2 text-xs font-medium text-slate-400 italic">vs last month</span>
          </div>
        </div>
      </div>

      <!-- Top Motivation & Production Champions Section -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <!-- Best Worker Card -->
        <div class="bg-gradient-to-br from-amber-500 via-amber-600 to-orange-600 rounded-2xl p-6 text-white shadow-lg shadow-amber-500/20 relative overflow-hidden group">
          <div class="absolute -right-8 -bottom-8 w-36 h-36 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
          
          <div class="relative z-10 flex flex-col justify-between h-full">
            <div class="flex items-center justify-between mb-4">
              <span class="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-black uppercase tracking-wider text-amber-100">
                ⭐ Worker of the Period
              </span>
              <span v-if="dashboardData?.top_worker" class="text-xs font-black bg-amber-300 text-slate-900 px-2.5 py-1 rounded-lg">
                {{ dashboardData.top_worker.composite_score }} / 100 PTS
              </span>
            </div>

            <div v-if="dashboardData?.top_worker" class="flex items-center gap-4 my-2">
              <div class="w-14 h-14 rounded-xl bg-white text-amber-600 flex items-center justify-center font-black text-xl shadow-md shrink-0">
                {{ dashboardData.top_worker.full_legal_name.charAt(0) }}
              </div>
              <div>
                <h3 class="text-xl font-black text-white tracking-tight leading-tight">
                  {{ dashboardData.top_worker.full_legal_name }}
                </h3>
                <p class="text-amber-100 font-semibold text-xs mt-0.5">
                  {{ dashboardData.top_worker.job_title }} • {{ dashboardData.top_worker.department }}
                </p>
                <div class="flex items-center gap-3 mt-1.5 text-xs font-bold text-amber-200">
                  <span>✓ {{ dashboardData.top_worker.tasks_completed }} Tasks Done</span>
                  <span>•</span>
                  <span>⭐ {{ dashboardData.top_worker.avg_rating }} Rating</span>
                </div>
              </div>
            </div>
            <div v-else class="py-4 text-amber-100 text-sm font-semibold">
              No worker performance recorded for this period.
            </div>

            <div class="mt-4 pt-4 border-t border-white/15 flex items-center justify-between">
              <span class="text-xs font-semibold text-amber-100">Motivate & Award Outstanding Staff</span>
              <RouterLink to="/workers/leaderboard" class="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white text-slate-900 font-bold text-xs rounded-lg hover:bg-amber-50 transition-colors shadow">
                Leaderboard →
              </RouterLink>
            </div>
          </div>
        </div>

        <!-- Best Department Card -->
        <div class="bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800 rounded-2xl p-6 text-white shadow-lg shadow-indigo-500/20 relative overflow-hidden group">
          <div class="absolute -right-8 -bottom-8 w-36 h-36 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>

          <div class="relative z-10 flex flex-col justify-between h-full">
            <div class="flex items-center justify-between mb-4">
              <span class="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-black uppercase tracking-wider text-indigo-200">
                🏆 Top Producing Department
              </span>
              <span v-if="dashboardData?.top_department" class="text-xs font-black bg-indigo-300 text-slate-900 px-2.5 py-1 rounded-lg">
                {{ dashboardData.top_department.avg_score }} / 100 AVG
              </span>
            </div>

            <div v-if="dashboardData?.top_department" class="my-1">
              <h3 class="text-xl font-black text-white tracking-tight leading-tight">
                {{ dashboardData.top_department.department }} Department
              </h3>
              <p class="text-indigo-200 font-semibold text-xs mt-0.5">
                Top Contributor: <span class="text-white font-bold">{{ dashboardData.top_department.top_worker_name }}</span>
              </p>
              <div class="grid grid-cols-2 gap-3 mt-3">
                <div class="bg-white/10 rounded-lg p-2.5 backdrop-blur-sm">
                  <p class="text-[10px] font-black uppercase text-indigo-200">Staff Count</p>
                  <p class="text-base font-black text-white">{{ dashboardData.top_department.worker_count }} Personnel</p>
                </div>
                <div class="bg-white/10 rounded-lg p-2.5 backdrop-blur-sm">
                  <p class="text-[10px] font-black uppercase text-indigo-200">Task Completion</p>
                  <p class="text-base font-black text-white">{{ dashboardData.top_department.completion_rate }}%</p>
                </div>
              </div>
            </div>
            <div v-else class="py-4 text-indigo-200 text-sm font-semibold">
              No department production data recorded for this period.
            </div>

            <div class="mt-4 pt-4 border-t border-white/15 flex items-center justify-between">
              <span class="text-xs font-semibold text-indigo-200">Department Performance</span>
              <RouterLink to="/workers/leaderboard" class="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white text-indigo-900 font-bold text-xs rounded-lg hover:bg-indigo-50 transition-colors shadow">
                Full Rankings →
              </RouterLink>
            </div>
          </div>
        </div>
      </div>


      <!-- Main Layout -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Recent Activity -->
        <div class="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div class="px-6 py-5 border-b border-slate-50 flex items-center justify-between">
            <div class="flex items-center space-x-2">
              <div class="w-1.5 h-6 bg-blue-600 rounded-full"></div>
              <h3 class="text-lg font-bold text-slate-800 tracking-tight">System Feed</h3>
            </div>
            <button
              class="text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
              @click="fetchDashboardData"
            >
              Sync Now
            </button>
          </div>
          <div class="divide-y divide-slate-50 max-h-[500px] overflow-y-auto">
            <div v-if="loading" class="p-8 text-center">
              <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
              <p class="mt-2 text-slate-500 font-medium">Loading activity...</p>
            </div>
            <div v-else-if="dashboardData?.recent_activities.length === 0" class="p-12 text-center">
              <FontAwesomeIcon icon="clock" class="h-12 w-12 text-slate-200 mb-3" />
              <p class="text-slate-500">No recent activities found.</p>
            </div>
            <div
              v-for="activity in dashboardData?.recent_activities"
              :key="activity.id"
              class="px-6 py-5 hover:bg-slate-50/50 transition-colors group"
            >
              <div class="flex items-center">
                <div
                  :class="[
                    activity.type === 'sale' ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600',
                    'p-2.5 rounded-full mr-4 group-hover:scale-110 transition-transform'
                  ]"
                >
                  <FontAwesomeIcon
                    :icon="activity.type === 'sale' ? 'check-circle' : 'clipboard-list'"
                    class="h-5 w-5"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-bold text-slate-800 group-hover:text-blue-700 transition-colors">
                    {{ activity.action }}
                  </p>
                  <div class="flex items-center mt-1 space-x-3">
                    <p class="text-xs font-medium text-slate-500 flex items-center">
                      <FontAwesomeIcon icon="users" class="mr-1.5 text-slate-400" />
                      {{ activity.user }}
                    </p>
                    <span class="text-slate-200">•</span>
                    <p class="text-xs font-medium text-slate-400 flex items-center">
                      <FontAwesomeIcon icon="clock" class="mr-1.5" />
                      {{ activity.time_str }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Quick Actions Side Column -->
        <div class="space-y-6">
          <div class="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <h3 class="text-lg font-bold text-slate-800 mb-6 flex items-center">
              <span class="w-1.5 h-6 bg-amber-500 rounded-full mr-2"></span>
              Pulse Actions
            </h3>
            <div class="space-y-3">
              <RouterLink
                to="/shops/butcher/sales"
                class="flex items-center p-3 rounded-xl border border-slate-50 hover:border-blue-100 hover:bg-blue-50/50 transition-all group"
              >
                <div class="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center mr-3 group-hover:bg-blue-600 group-hover:text-white transition-colors shadow-sm">
                  <FontAwesomeIcon icon="clipboard-list" />
                </div>
                <span class="font-bold text-slate-700 group-hover:text-blue-800">Shop Sales</span>
              </RouterLink>

              <RouterLink
                to="/shops/products"
                class="flex items-center p-3 rounded-xl border border-slate-50 hover:border-amber-100 hover:bg-amber-50/50 transition-all group"
              >
                <div class="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center mr-3 group-hover:bg-amber-600 group-hover:text-white transition-colors shadow-sm">
                  <FontAwesomeIcon icon="boxes" />
                </div>
                <span class="font-bold text-slate-700 group-hover:text-amber-800">Inventory</span>
              </RouterLink>

              <RouterLink
                to="/supervisor/farm"
                class="flex items-center p-3 rounded-xl border border-slate-50 hover:border-emerald-100 hover:bg-emerald-50/50 transition-all group"
              >
                <div class="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mr-3 group-hover:bg-emerald-600 group-hover:text-white transition-colors shadow-sm">
                  <FontAwesomeIcon icon="calendar-alt" />
                </div>
                <span class="font-bold text-slate-700 group-hover:text-emerald-800">Farm Ops</span>
              </RouterLink>

              <RouterLink
                to="/workers"
                class="flex items-center p-3 rounded-xl border border-slate-50 hover:border-rose-100 hover:bg-rose-50/50 transition-all group"
              >
                <div class="w-10 h-10 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center mr-3 group-hover:bg-rose-600 group-hover:text-white transition-colors shadow-sm">
                  <FontAwesomeIcon icon="users" />
                </div>
                <span class="font-bold text-slate-700 group-hover:text-rose-800">Staff Management</span>
              </RouterLink>
            </div>
          </div>
          
          <!-- System Status Mini Card -->
          <div class="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 text-white shadow-lg overflow-hidden relative">
            <div class="absolute -bottom-6 -right-6 w-32 h-32 bg-white/5 rounded-full"></div>
            <h4 class="text-sm font-bold uppercase tracking-widest text-slate-400 mb-2">Performance</h4>
            <div class="flex items-end justify-between">
              <div>
                <p class="text-2xl font-black">99.8%</p>
                <p class="text-xs text-emerald-400 font-bold mt-1">System Healthy</p>
              </div>
              <FontAwesomeIcon icon="chart-line" class="text-3xl text-slate-700" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shadow-opacity-20 {
  --tw-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
}
</style>
